import { useState } from 'react';
import { useLocation } from "wouter";
import { Target } from "lucide-react";

interface FormData {
  painPoints: string[];
  transformationGoals: string[];
  otherPainPoint: string;
  otherGoal: string;
}

const painPointsOptions = [
  "Manual journal entries",
  "Late adjustments after close",
  "Complex intercompany processing",
  "Data quality & reconciliation issues",
  "No orchestration or workflow tracking",
  "Audit trail and compliance gaps",
  "Resource constraints",
  "Other"
];

const goalsOptions = [
  "Accelerate close timeline",
  "Improve control & auditability",
  "Automate manual tasks",
  "Reduce rework and errors",
  "Standardize global processes",
  "Improve team productivity",
  "Better visibility & reporting",
  "Other"
];

export default function TransformationGoals() {
  console.log('TransformationGoals component loading...');
  const [location, setLocation] = useLocation();
  const [formData, setFormData] = useState<FormData>({
    painPoints: [],
    transformationGoals: [],
    otherPainPoint: '',
    otherGoal: ''
  });

  const handlePainPointChange = (option: string) => {
    setFormData(prev => ({
      ...prev,
      painPoints: prev.painPoints.includes(option)
        ? prev.painPoints.filter(item => item !== option)
        : [...prev.painPoints, option]
    }));
  };

  const handleGoalChange = (option: string) => {
    setFormData(prev => ({
      ...prev,
      transformationGoals: prev.transformationGoals.includes(option)
        ? prev.transformationGoals.filter(item => item !== option)
        : [...prev.transformationGoals, option]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem('transformationGoals', JSON.stringify(formData));
    setLocation('/value/modeling');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 bg-primary">
            <Target className="text-white" size={32} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-6">
            Transformation Goals
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Help us understand where you're aiming to take your Record-to-Report operations
          </p>
        </div>

        {/* Form */}
        <div className="bg-card rounded-xl shadow-xl border border-border p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Two-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current pain points */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-primary tracking-tight mb-2">
                    Current pain points
                  </h3>
                  <div className="w-16 h-px bg-primary/30 mb-4"></div>
                </div>
                <div className="space-y-3">
                  {painPointsOptions.map((option) => (
                    <div key={option}>
                      <label className="group relative flex items-center p-4 bg-surface/30 rounded-xl border border-border hover:bg-surface/50 hover:border-green-500 hover:shadow-md hover:shadow-green-300/40 transition-all duration-150 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.painPoints.includes(option)}
                            onChange={() => handlePainPointChange(option)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${
                            formData.painPoints.includes(option)
                              ? 'bg-primary border-primary ring-2 ring-primary/20'
                              : 'border-muted bg-surface group-hover:border-primary/50'
                          }`}>
                            {formData.painPoints.includes(option) && (
                              <svg className="w-3 h-3 text-foreground absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors">{option}</span>
                      </label>
                      
                      {/* Dynamic input for "Other" */}
                      {option === "Other" && formData.painPoints.includes("Other") && (
                        <div className="mt-3 ml-8">
                          <input
                            type="text"
                            value={formData.otherPainPoint}
                            onChange={(e) => setFormData(prev => ({ ...prev, otherPainPoint: e.target.value }))}
                            className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                            placeholder="Please specify..."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Transformation goals */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-primary tracking-tight mb-2">
                    Transformation goals
                  </h3>
                  <div className="w-16 h-px bg-primary/30 mb-4"></div>
                </div>
                <div className="space-y-3">
                  {goalsOptions.map((option) => (
                    <div key={option}>
                      <label className="group relative flex items-center p-4 bg-surface/30 rounded-xl border border-border hover:bg-surface/50 hover:border-green-500 hover:shadow-md hover:shadow-green-300/40 transition-all duration-150 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.transformationGoals.includes(option)}
                            onChange={() => handleGoalChange(option)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${
                            formData.transformationGoals.includes(option)
                              ? 'bg-primary border-primary ring-2 ring-primary/20'
                              : 'border-muted bg-surface group-hover:border-primary/50'
                          }`}>
                            {formData.transformationGoals.includes(option) && (
                              <svg className="w-3 h-3 text-foreground absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors">{option}</span>
                      </label>

                      {/* Dynamic input for "Other" */}
                      {option === "Other" && formData.transformationGoals.includes("Other") && (
                        <div className="mt-3 ml-8">
                          <input
                            type="text"
                            value={formData.otherGoal}
                            onChange={(e) => setFormData(prev => ({ ...prev, otherGoal: e.target.value }))}
                            className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                            placeholder="Please specify..."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Continue button */}
            <div className="pt-8 flex justify-center">
              <button
                type="submit"
                className="bg-primary text-black hover:bg-primary/90 transition font-semibold py-4 px-8 rounded-button shadow-lg duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-card transform hover:scale-[1.02] hover:shadow-xl"
              >
                Continue to Value Modeling
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}